import { Spin } from 'antd';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import React, {Suspense, useEffect, useMemo, useRef, useState} from 'react';
import MobileDetect from 'mobile-detect';
import { OpenAiModal } from './OpenAiModal';
import {
  JobInterestFormFields,
  getJobInterestFormDefaultValuesIfNull,
} from './JobInterestFormFields';
// import { useFlag } from '../../../hooks/use-flag';
import { logICFSnapShot } from '../job-interest/job-interest.actions';
import { ToastService } from '../../../services/ToastService';
import {customQuestionInputTypes} from "./CustomQuestionField";

function debounce(func, wait, immediate) {
  let timeout;

  return function() {
      const context = this, args = arguments;

      const later = function() {
          timeout = null;
          if (!immediate) func.apply(context, args);
      };

      const callNow = immediate && !timeout;

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);

      if (callNow) func.apply(context, args);
  };
}


export const validateJobInterestForm = ({
  values = {},
  isAvailabilityUnknown,
  isDeveloperAvailabilityPresent,
  developerAvailability,
  showOldCustomQuestion,
  isProxyApplication = false,
  enableAcademicFields,
  disablePerfectFitQuestion,
}) => {
  const errors = {};

  if (values.isInterested) {
    if (!values.availabilityConfirmed)
      errors.availabilityConfirmed = 'Availability confirmation is required';
    if (!values.advancedConfiguration) {
      if (isAvailabilityUnknown) errors.availabilityConfirmed = 'Availability is required';
      if (!values.salaryConfirmed) errors.salaryConfirmed = 'Rate is required';
      if (!isDeveloperAvailabilityPresent)
        errors.availabilityConfirmed = 'Kindly update availability';
      if (developerAvailability && developerAvailability.availabilityAnswer === 'not-looking-for-job')
        errors.availabilityConfirmed = 'Kindly update availability';
      if (!disablePerfectFitQuestion) {
        if (!values || !values.perfectFitQuestionAnswer || !values.perfectFitQuestionAnswer.trim())
          errors.perfectFitQuestionAnswer = 'Answer is required';
      }

      if (values.canOverlapWithTimezone === null || values.canOverlapWithTimezone === undefined) {
        errors.canOverlapWithTimezone = 'Timezone overlap confirmation is required';
      }

      if (enableAcademicFields) {
        if (values.scoreOption === 'gpa') {
          if (!values.maxAttainableGPA) errors.maxAttainableGPA = 'Max attainable GPA is required';
          if (!values.developerGPAScore) errors.developerGPAScore = 'GPA score is required';
        } else if (values.scoreOption === 'percentage') {
          if (!values.percentage) errors.percentage = 'Percentage is required';
        }
        if (!values.gradingExtraDetails) errors.gradingExtraDetails = 'Grading details are required';
      }
    }

    if (!isProxyApplication && !values.dataIntegrityConfirmed)
      errors.dataIntegrityConfirmed = 'Required';

    if (!showOldCustomQuestion) {
      if (!values.developerTimezoneId) errors.developerTimezoneId = 'Timezone is required';

      if ((values.customQuestionAnswerJson || []).length) {
        const answeredAllRequiredCustomQuestions = (values.customQuestionAnswerJson || [])
          .filter((question) => question.isRequired)
          .filter((question) => question.inputType !== customQuestionInputTypes.INTEREST)
          .every((question) => {
            if (question.inputType === customQuestionInputTypes.TEXT) {
              return (question.answer || '').trim();
            }
            if (question.inputType === customQuestionInputTypes.NOTICE_PERIOD) {
              return typeof question.answer === 'number';
            }
            return question.answer;
          });
        if (!answeredAllRequiredCustomQuestions) errors.customQuestionAnswerJson = 'Required';
      }
    } else {
      if (!(!values.customQuestion || (values.customQuestion && values.customQuestionAnswer)))
        errors.customQuestionAnswer = 'Required';
    }
  } else {
    if (!values.notInterestedReasonId) errors.notInterestedReasonId = 'Reason is required';
  }
  return errors;
};

export function JobInterestForm({
  jobInterest,
  developerInfo,
  developerAvailability,
  developerRateDetails,
  onSubmit,
  notInterestedReasons,
  setDeveloperAvailability,
  setDeveloperExpectedRateDetails,
  onDeveloperAvailabilityChange,
  saveRateChangeAttempt,
  createdByEmail,
  isVetsmithFlow,
  jobMatchingVetsmithFlag,
  timezones,
  jobInfo,
  blockSubmissionReason,
}) {
  const [deviceUsed, setDeviceUsed] = useState();
  const [openAiPopupVisible, setOpenAiPopupVisible] = useState(false);
  const [isNegotiatedOrNewDesiredSalaryPresent, setIsNegotiatedOrNewDesiredSalaryPresent] =
    useState(false);
  const [isNegotiatedOrNewDesiredSalaryPresentV2, setIsNegotiatedOrNewDesiredSalaryPresentV2] =
    useState(false);
  const [isDeveloperAvailabilityPresent, setIsDeveloperAvailabilityPresent] = useState(false);
  const [isAvailabilityUnknown, setIsAvailabilityUnknown] = useState(false);
  const lastTouched = useRef(null);
  const [formValues, setFormValues] = useState({});
  const [isSubmitAllowed, setIsSubmitAllowed] = useState(true);
  const enableIcfRateV2 = true;
  const enableAcademicFields = false;
  const disablePerfectFitQuestion = true;

  useEffect(() => {
    const type = new MobileDetect(window.navigator.userAgent);
    if (type.os() === 'iOS') {
      setDeviceUsed('Mobile device - iOS');
    } else if (type.os() === 'AndroidOS') {
      setDeviceUsed('Mobile device - Android');
    } else if (type.os() === 'BlackBerryOS') {
      setDeviceUsed('Mobile device - BlackBerry');
    } else if (type.os() === 'WindowsOS') {
      setDeviceUsed('Desktop - Windows');
    } else if (type.os() === 'MacOS') {
      setDeviceUsed('Desktop - Mac');
    } else {
      setDeviceUsed('Desktop');
    }
  }, []);

  if (Object.keys(jobInterest).length !== 0 && !jobInterest.isSubmitAllowed && isSubmitAllowed) {
    setIsSubmitAllowed(false);
    // setTimeout(() => ToastService.info('Form is already been submitted'), 4000);
  }

  const jobInterestDefaultDetails = useMemo(() => getJobInterestFormDefaultValuesIfNull(
    { ...jobInterest },
    developerInfo,
    isNegotiatedOrNewDesiredSalaryPresent,
    isDeveloperAvailabilityPresent,
    developerAvailability,
    timezones,
    false,
    isNegotiatedOrNewDesiredSalaryPresentV2,
    enableIcfRateV2,
  ), [
    jobInterest,
    developerInfo,
    isNegotiatedOrNewDesiredSalaryPresent,
    isDeveloperAvailabilityPresent,
    developerAvailability,
    timezones,
    isNegotiatedOrNewDesiredSalaryPresentV2,
    enableIcfRateV2
  ]);

  const handleOpenAiOk = () => {
    if (disablePerfectFitQuestion) {
      delete formValues.perfectFitQuestionAnswer;
    }
    onSubmit(formValues);
  };

  const handleClose = () => {
    setOpenAiPopupVisible(false);
  };

  const autoSaveForm = debounce((formData) => {
    if (!jobInterest.idHash || jobInterest.submittedAt) {
      return;
    }
    const savedFormData = localStorage.getItem(`icf_form_${jobInterest.idHash}`);
    const savedFormDataObj = JSON.parse(savedFormData);
    const timestamp = Date.now();
    localStorage.setItem(
      `icf_form_${jobInterest.idHash}`,
      JSON.stringify({
        formData,
        timestamp,
        developerAvailability: (savedFormDataObj || {}).developerAvailability,
      }),
    );
  }, 1000);

  const logSnapShot = debounce((formData) => {
    if (!jobInterest.id || jobInterest.submittedAt || !lastTouched.current) {
      return;
    }
    logICFSnapShot(jobInterest.id, {
      formValues: formData,
      lastUpdatedField: lastTouched.current,
    });
  }, 2000);

  return (
    <>
      <Form
        initialValues={jobInterestDefaultDetails}
        onSubmit={(values) => {
          const errors = validateJobInterestForm({
            values,
            isAvailabilityUnknown,
            isDeveloperAvailabilityPresent,
            developerAvailability,
            enableAcademicFields,
            disablePerfectFitQuestion,
            showOldCustomQuestion:
              jobInterestDefaultDetails.customQuestion &&
              !(jobInterestDefaultDetails.customQuestionAnswerJson || []).length > 0,
            isProxyApplication: false,
          });
          const hasErrors = Object.keys(errors).length > 0;
          if (hasErrors) return;
          if (isVetsmithFlow && values.isInterested) {
            setFormValues({
              ...values,
              ...{ availability: developerAvailability },
              ...{ deviceUsed },
            });
            setOpenAiPopupVisible(true);
          } else {
            if (disablePerfectFitQuestion) {
              delete values.perfectFitQuestionAnswer;
            }
            onSubmit({
              ...values,
              ...{ availability: developerAvailability },
              ...{ deviceUsed },
            });
          }
        }}
        mutators={{
          ...arrayMutators,
        }}
        subscription={{
          handleSubmit: true,
          values: true,
          submitting: true,
          pristine: false,
          active: true,
        }}
        render={({ handleSubmit, submitting, pristine, values, active }) => {
          // autoSaveForm(values);
          logSnapShot(values);
          if (active && active !== lastTouched.current) lastTouched.current = active;
          const errors = validateJobInterestForm({
            values,
            isAvailabilityUnknown,
            isDeveloperAvailabilityPresent,
            developerAvailability,
            enableAcademicFields,
            disablePerfectFitQuestion,
            showOldCustomQuestion:
              jobInterestDefaultDetails.customQuestion &&
              !(jobInterestDefaultDetails.customQuestionAnswerJson || []).length > 0,
            isProxyApplication: false,
          });
          return (
            <form className="job-interest-form__container" onSubmit={handleSubmit}>
              <JobInterestFormFields
                isProxyApplication={false}
                submitting={submitting}
                pristine={pristine}
                values={values}
                jobInterest={jobInterest}
                jobInterestDefaultDetails={jobInterestDefaultDetails}
                developerInfo={developerInfo}
                developerAvailability={developerAvailability}
                developerRateDetails={developerRateDetails}
                notInterestedReasons={notInterestedReasons}
                setDeveloperAvailability={setDeveloperAvailability}
                setDeveloperExpectedRateDetails={setDeveloperExpectedRateDetails}
                onDeveloperAvailabilityChange={onDeveloperAvailabilityChange}
                saveRateChangeAttempt={saveRateChangeAttempt}
                createdByEmail={createdByEmail}
                isVetsmithFlow={isVetsmithFlow}
                jobMatchingVetsmithFlag={jobMatchingVetsmithFlag}
                isAvailabilityUnknown={isAvailabilityUnknown}
                isSubmitAllowed={isSubmitAllowed}
                setIsAvailabilityUnknown={setIsAvailabilityUnknown}
                formErrors={errors}
                isNegotiatedOrNewDesiredSalaryPresent={isNegotiatedOrNewDesiredSalaryPresent}
                isNegotiatedOrNewDesiredSalaryPresentV2={isNegotiatedOrNewDesiredSalaryPresentV2}
                setIsNegotiatedOrNewDesiredSalaryPresent={setIsNegotiatedOrNewDesiredSalaryPresent}
                setIsNegotiatedOrNewDesiredSalaryPresentV2={
                  setIsNegotiatedOrNewDesiredSalaryPresentV2
                }
                isDeveloperAvailabilityPresent={isDeveloperAvailabilityPresent}
                setIsDeveloperAvailabilityPresent={setIsDeveloperAvailabilityPresent}
                timezones={timezones}
                jobInfo={jobInfo}
                blockSubmissionReason={blockSubmissionReason}
              />
            </form>
          );
        }}
      />

      {openAiPopupVisible && (
        <Suspense fallback={<Spin size="large" />}>
          <OpenAiModal onOk={handleOpenAiOk} onCancel={handleClose} />
        </Suspense>
      )}
    </>
  );
}
